import { useMemo, useState } from "react";
import { EmptyState } from "@/ui/patterns";
import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";
import { OfferDayGroup } from "./OfferDayGroup";
import type { PartnerOffer } from "@/domain/offer/types";
import { groupOffersByDay, offerStage } from "@/domain/offer/rules";

interface OfferListProps {
  offers: PartnerOffer[];
  canTrade: boolean;
  highlightedOfferId?: number | null;
  onEdit: (offer: PartnerOffer) => void;
  onCancel: (offer: PartnerOffer) => void;
}

export function OfferList({
  offers,
  canTrade,
  highlightedOfferId = null,
  onEdit,
  onCancel,
}: OfferListProps) {
  const [hideEnded, setHideEnded] = useState(true);
  const now = useMemo(() => new Date(), []);

  const endedCount = useMemo(
    () =>
      offers.filter((offer) => offerStage(offer.startTime, offer.endTime, now) === "ended").length,
    [offers, now],
  );

  const days = useMemo(() => {
    const shown = hideEnded
      ? offers.filter((offer) => offerStage(offer.startTime, offer.endTime, now) !== "ended")
      : offers;
    return groupOffersByDay(shown, now);
  }, [offers, hideEnded, now]);

  if (offers.length === 0) {
    return (
      <div className="border border-dashed border-border rounded-xl max-w-2xl mx-auto">
        <EmptyState
          variant="hero"
          title="Tady bude první vyprodaná hodina."
          description="Last-minute slevový termín se v aplikaci objeví do minuty od vypsání."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2 pb-1">
        <Switch id="hide-ended" checked={hideEnded} onCheckedChange={setHideEnded} />
        <Label htmlFor="hide-ended" className="text-sm cursor-pointer">
          Skrýt ukončené
          {endedCount > 0 && <span className="ml-1 text-muted-foreground">({endedCount})</span>}
        </Label>
      </div>

      {days.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl px-6 py-12 text-center">
          <p className="text-muted-foreground">
            {hideEnded && endedCount > 0
              ? "Všechny slevové termíny jsou ukončené. Zobrazte je přepínačem výše."
              : "Žádné slevové termíny k zobrazení."}
          </p>
        </div>
      ) : (
        days.map((day) => (
          <OfferDayGroup
            key={day.date.getTime()}
            day={day}
            canTrade={canTrade}
            highlightedOfferId={highlightedOfferId}
            now={now}
            onEdit={onEdit}
            onCancel={onCancel}
          />
        ))
      )}
    </div>
  );
}
